import {
  PerformanceQuestionSingleLabelInput,
  QuestionnaireContentFragment,
} from 'generated/resources/graphql'
import { mapValues } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import {
  type PerformanceRawQuestionnaires,
  type StrategiesQuestionnaires,
  type StrategyQuestionnaire,
  useQuestionnairesRawContent,
} from 'modules/performance_process/hooks/useQuestionnairesRawContent'
import type {
  Question,
  QuestionnaireElement,
  Section,
  Translations,
} from 'modules/questionnaires/lib/types'

type PerformanceQuestionSingleLabel = QuestionnaireContentFragment['questions'][number]

function mapQuestion(
  q: PerformanceQuestionSingleLabel | PerformanceQuestionSingleLabelInput
): Question {
  return {
    uuid: q.uuid,
    title: q.title,
    scale: q?.scale?.map((s) => ({ value: s.value, text: s.text })),
    answerType: q.answerType,
    maxChoices: q.maxChoices || null,
    choiceOptions: q.choiceOptions || [],
    mandatory: q.mandatory,
    withComment: q.withComment,
  }
}

function mapSection(sectionContent: QuestionnaireContentFragment, forceLock = false): Section {
  return {
    uuid: sectionContent.uuid,
    locked: forceLock,
    title: sectionContent.sectionTitle ?? '',
    questions: sectionContent.questions.map(mapQuestion),
  }
}

function mapQuestionnaire(
  content: QuestionnaireContentFragment[],
  sectionMapper: (item: QuestionnaireContentFragment) => Section = mapSection,
  questionMapper: (
    item: PerformanceQuestionSingleLabel | PerformanceQuestionSingleLabelInput
  ) => Question = mapQuestion
): QuestionnaireElement[] {
  return content
    .map((item) => {
      if (item.type === 'section') return sectionMapper(item)

      const firstQuestion = item.questions[0]
      if (!firstQuestion) return null

      return questionMapper(firstQuestion)
    })
    .filter(Boolean)
}

function mapStrategyQuestionnaire(
  strategyContent: StrategyQuestionnaire<QuestionnaireContentFragment>
): StrategyQuestionnaire<QuestionnaireElement> {
  return mapValues(strategyContent, (content, strategy) =>
    mapQuestionnaire(content, (section) => mapSection(section, strategy != 'general'))
  )
}

export const getQuestionnaireTranslations = (): Translations => {
  return {
    questionnaire: {
      add_question: i18n.t('performance_process.reviews.questionnaire_add_question'),
      add_section: i18n.t('performance_process.reviews.questionnaires_by_strategy.add_section'),
      empty_state: {
        title: i18n.t('questionnaires.questionnaire_response.empty_state.message'),
        description: '',
      },
    },
    section: {
      questions_count: ({ count }) =>
        i18n.tp('performance_process.reviews.questionnaires_by_strategy.section.questions_count', {
          count,
        }),
      questions: i18n.t('performance_process.reviews.questionnaires_by_strategy.section.questions'),
      question: i18n.t('performance_process.reviews.questionnaires_by_strategy.question'),
      add_question: i18n.t(
        'performance_process.reviews.questionnaires_by_strategy.section.add_question'
      ),
      title: {
        placeholder: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.title_placeholder'
        ),
        save: i18n.t('performance_process.reviews.questionnaires_by_strategy.section.title_save'),
        copy: i18n.t('performance_process.reviews.questionnaires_by_strategy.section.title_copy'),
      },
      toggle: i18n.t('performance_process.reviews.questionnaires_by_strategy.section.toggle'),
      default_actions: {
        edit: i18n.t('performance_process.reviews.questionnaires_by_strategy.default_actions.edit'),
        duplicate: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.default_actions.duplicate'
        ),
        delete: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.default_actions.delete'
        ),
      },
      delete_modal: {
        title: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.delete_modal.title'
        ),
        description: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.delete_modal.description'
        ),
        confirm: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.delete_modal.confirm'
        ),
        cancel: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.delete_modal.cancel'
        ),
      },
    },
    question: {
      mandatory: i18n.t('performance_process.reviews.mandatory'),
      copy_placeholder: i18n.t(
        'performance_process.reviews.questionnaires_by_strategy.questions.copy_placeholder'
      ),
      label: i18n.t('performance_process.reviews.questionnaires_by_strategy.question'),
      placeholder: i18n.t(
        'performance_process.reviews.questionnaires_by_strategy.questions.placeholder'
      ),
      description: {
        label: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.question_description.label'
        ),
        placeholder: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.question_description.placeholder'
        ),
      },
      optional_comment: {
        label: i18n.t('performance_process.reviews.participants_can_comment'),
        information: i18n.t('performance_process.reviews.comment_required_info'),
      },
      multiple_choice_configurator: {
        add_option: i18n.t('performance_process.reviews.question_types.add_option'),
        placeholder: i18n.t('performance_process.reviews.question_types.add_option_placeholder'),
      },
      types: {
        number: i18n.t('performance_process.reviews.question_types.number'),
        multiple_choice: i18n.t('performance_process.reviews.question_types.multiple_choice'),
        text: i18n.t('performance_process.reviews.question_types.text'),
        rating: i18n.t('performance_process.reviews.question_types.rating'),
        single_choice: i18n.t('performance_process.reviews.question_types.single_choice'),
      },
      type_selector: {
        label: i18n.t('performance_process.reviews.question_types.type_of_answer'),
        placeholder: i18n.t('performance_process.reviews.question_types.select_type'),
        blank_label: i18n.t('performance_process.reviews.question_types.blank'),
      },
      actions: {
        edit: i18n.t('performance_process.reviews.questionnaires_by_strategy.default_actions.edit'),
        duplicate: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.default_actions.duplicate'
        ),
        delete: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.default_actions.delete'
        ),
      },
      save: i18n.t('performance_process.reviews.questionnaires_by_strategy.questions.save'),
      validations: {
        title: {
          required: i18n.t('performance_process.reviews.question_types.errors.title_required'),
        },
        answer_type: {
          choice_options: {
            min_two_options: i18n.t(
              'performance_process.reviews.question_types.errors.min_two_options'
            ),
          },
        },
        options: {
          required: i18n.t(
            'performance_process.reviews.question_types.errors.required_choice_option'
          ),
          repeated: i18n.t(
            'performance_process.reviews.question_types.errors.repeated_choice_option'
          ),
        },
      },
    },
  }
}

export type MappedStrategyQuestionnaire = StrategyQuestionnaire<QuestionnaireElement>
export type MappedQuestionnaires = StrategiesQuestionnaires<QuestionnaireElement>
/**
 * Hook for transforming raw performance questionnaires into the interface required by frontend questionnaire components.
 *
 * The backend returns questionnaires using a common `QuestionnaireElement` interface (since it cannot return
 * arrays of different types). This hook transforms these elements into the specific section and question
 * structures expected by the questionnaire components.
 *
 * Additionally, the backend embeds strategy configurations within each questionnaire, while the frontend
 * requires questionnaires organized by strategy. This hook performs both transformations, providing
 * a unified interface where questionnaires are properly typed and keyed by strategy.
 *
 */
export const useMappedQuestionnaires = (rawQuestionnaires: PerformanceRawQuestionnaires) => {
  const { rawQuestionnairesContentByStrategies, totalQuestionsByStrategies } =
    useQuestionnairesRawContent(rawQuestionnaires)

  const mappedQuestionnairesByStrategies: StrategiesQuestionnaires<QuestionnaireElement> =
    mapValues(rawQuestionnairesContentByStrategies, (content) => mapStrategyQuestionnaire(content))

  return {
    mappedQuestionnairesByStrategies,
    totalQuestionsByStrategies,
  }
}
